// Sugalarm — environment-derived URLs.
//
// In dev, Vite proxies /api and /ws to the FastAPI server, so relative
// URLs work everywhere. In production the SPA is served by FastAPI
// from the same origin, so relative URLs also work everywhere — for
// the kiosk and for the browser PWA.
//
// Cycle 26 added a third runtime surface: the Capacitor native shell.
// Inside the webview the host is `capacitor://localhost` (iOS) or
// `https://localhost` (Android); a relative `/api/...` URL would
// resolve against that non-existent local server. When
// `isCapacitorNative()` is true we rewrite to the Fly origin
// explicitly. Same for the WebSocket scheme + host.

import { isCapacitorNative } from "./lib/capacitor";

const FLY_HOST = "sugalarm.fly.dev";
const FLY_API_BASE = `https://${FLY_HOST}`;

// On-device bench testing (default OFF — production builds are unchanged).
//
// The absolute-origin rewrite below is right for the SHIPPED shell, whose
// webview origin is a pseudo-host (`capacitor://localhost` / `https://localhost`)
// with no server behind it. It is WRONG when the shell has been pointed at a
// local `vite preview` over `adb reverse`: that origin is a real server which
// proxies /api and /ws itself, so rewriting to the Fly origin turns every call
// into a cross-origin request and the SPA dies on CORS at `/api/auth/me`.
//
// ⚠ THE PROXY IS THE ANSWER TO THAT CORS ERROR, NOT THE ALLOWLIST. Never add
// localhost to the production CORS allowed-origins list to make a bench test
// pass. Build with VITE_LOCAL_API=1 instead, which keeps URLs relative so the
// preview proxy can serve them same-origin.
const LOCAL_API = import.meta.env.VITE_LOCAL_API === "1";

export const apiUrl = (path: string): string => {
  const suffix = path.startsWith("/") ? path : `/${path}`;
  if (isCapacitorNative() && !LOCAL_API) return `${FLY_API_BASE}${suffix}`;
  return suffix; // same-origin / proxied
};

export const wsUrl = (path: string, token: string): string => {
  const isNative = isCapacitorNative() && !LOCAL_API;
  const proto =
    isNative || window.location.protocol === "https:" ? "wss:" : "ws:";
  const host = isNative ? FLY_HOST : window.location.host;
  const sep = path.includes("?") ? "&" : "?";
  return `${proto}//${host}${path}${sep}token=${encodeURIComponent(token)}`;
};
